import { useEffect, useMemo, useRef, useState } from 'react'
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline'

import { useI18n } from '../contexts/I18nContext'

export interface YearMonthValue {
  year: number
  month: number
}

interface YearMonthPickerProps extends YearMonthValue {
  onChange: (value: YearMonthValue) => void
  startYear?: number
  monthCount?: number
  ariaLabel?: string
  className?: string
}

const currentYear = new Date().getFullYear()
const defaultStartYear = currentYear - 2
const defaultMonthCount = 72

export default function YearMonthPicker({
  year,
  month,
  onChange,
  startYear = defaultStartYear,
  monthCount = defaultMonthCount,
  ariaLabel,
  className,
}: YearMonthPickerProps) {
  const { locale, t } = useI18n()
  const [isOpen, setIsOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)

  const periodFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric' }),
    [locale],
  )
  const periodOptions = useMemo(() => Array.from({ length: monthCount }, (_, index) => {
    const date = new Date(startYear, index, 1)

    return {
      key: `${date.getFullYear()}-${date.getMonth() + 1}`,
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      label: periodFormatter.format(date),
    }
  }), [monthCount, periodFormatter, startYear])
  const selectedPeriodLabel = periodFormatter.format(new Date(year, month - 1, 1))

  useEffect(() => {
    if (!isOpen) return

    const selectedOption = panelRef.current?.querySelector('[aria-selected="true"]')
    selectedOption?.scrollIntoView({ block: 'center' })
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    function handlePointerDown(event: PointerEvent): void {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  return (
    <div ref={rootRef} className={['year-month-picker', className].filter(Boolean).join(' ')}>
      <button
        type="button"
        className="field__input field__input--compact year-month-picker__trigger"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span>{selectedPeriodLabel}</span>
        <ChevronDownIcon />
      </button>
      {isOpen && (
        <div
          ref={panelRef}
          className="year-month-picker__panel"
          role="listbox"
          aria-label={ariaLabel ?? t('Year and month')}
        >
          {periodOptions.map((period) => {
            const selected = period.year === year && period.month === month

            return (
              <button
                key={period.key}
                type="button"
                className={`year-month-picker__option${selected ? ' year-month-picker__option--selected' : ''}`}
                role="option"
                aria-selected={selected}
                onClick={() => {
                  onChange({ year: period.year, month: period.month })
                  setIsOpen(false)
                }}
              >
                <span>{period.label}</span>
                {selected && <CheckIcon />}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}